import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// READ-ONLY on purpose: no "publish anyway" control, no override flag. The refusal lives in
// the frontend build (prebuild -> scripts/release_gate_check.js), so it cannot be lifted from
// here. A flaky test is named below to be FIXED, never to justify disabling the gate.
export default function ReleaseGatePanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/release-gate").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const rec = data.record || {};
  const green = rec.status === "pass";

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="release-gate-panel">
      <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
        <Lock className={`h-3.5 w-3.5 ${green ? "text-[#00FF87]" : "text-[#FF4B4B]"}`} />
        {t("admin.gate_title")}
      </span>
      <p className="mt-2 font-mono2 text-[10px] text-slate-600">{t("admin.gate_note")}</p>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1 font-mono2 text-[11px]">
        <span className={green ? "text-[#00FF87]" : "text-[#FF4B4B]"} data-testid="release-gate-status">
          {t(green ? "admin.gate_pass" : "admin.gate_refused")}
        </span>
        <span className="text-slate-400" data-testid="release-gate-passed">
          {rec.passed || 0} {t("admin.gate_tests")}
        </span>
        <span className="text-slate-600">{rec.at ? rec.at.slice(0, 16).replace("T", " ") : "—"}</span>
        <span className="text-slate-400" data-testid="release-gate-duration">
          {rec.duration_s ? `${rec.duration_s}s` : "—"}
          {data.median_duration_s ? (
            <span className="text-slate-600"> ({t("admin.gate_median")} {data.median_duration_s}s)</span>
          ) : null}
        </span>
        {data.slowdown_ratio >= 1.5 && (
          <span className="text-[#FFD600]" data-testid="release-gate-slowdown">
            {t("admin.gate_slower", { factor: data.slowdown_ratio })}
          </span>
        )}
        <span className="text-slate-600" data-testid="release-gate-streak">
          {t("admin.gate_streak")}: {data.green_streak}
        </span>
      </div>

      {(rec.failing_tests || []).length > 0 && (
        <ul className="mt-3 space-y-1 font-mono2 text-[10px] text-[#FF4B4B]" data-testid="release-gate-failing">
          {rec.failing_tests.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      )}

      {(data.flaky_tests || []).length > 0 && (
        <div className="mt-3" data-testid="release-gate-flaky">
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-[#FFD600]">
            {t("admin.gate_flaky")}
          </span>
          <ul className="mt-1 space-y-1 font-mono2 text-[10px] text-slate-300">
            {data.flaky_tests.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
